import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2, Loader2, ShieldCheck, FileDown, Smartphone, CreditCard } from "lucide-react";
import { api, apiError, eur } from "@/lib/apiClient";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function ProductRow({ it, checked, onToggle, locked }) {
  return (
    <div className="rounded-xl border p-4" data-testid={`pu-item-${it.id}`}>
      <label className="flex items-start gap-3">
        {!locked && <Checkbox checked={checked} onCheckedChange={onToggle} disabled={!it.accettabile} data-testid={`pu-check-${it.id}`} className="mt-1" />}
        <div className="flex-1">
          <div className="font-semibold text-forest">{it.product_name}</div>
          <div className="text-sm text-muted-foreground">Premio annuo lordo: <b className="text-forest">{eur(it.premio)}</b></div>
          {it.accettata && <div className="mt-1 flex items-center gap-1 text-xs font-semibold text-terracotta"><CheckCircle2 className="h-3.5 w-3.5" /> Accettata</div>}
          {locked && !it.accettata && <div className="mt-1 text-xs font-semibold text-muted-foreground" data-testid={`pu-not-accepted-${it.id}`}>Non accettata</div>}
        </div>
      </label>
      <div className="mt-3 flex flex-wrap gap-1.5">
        {it.documenti.map((d) => (
          <button key={d.kind} type="button" onClick={() => window.open(`${BACKEND}/api/public/proposta/${it.token}/download/${d.kind}`, "_blank")}
            data-testid={`pu-doc-${it.id}-${d.kind}`} className="flex items-center gap-1 rounded-md border px-2 py-1 text-[11px] text-forest hover:bg-sage">
            <FileDown className="h-3 w-3" /> {d.label}
          </button>
        ))}
      </div>
    </div>
  );
}

function PaymentBox({ p }) {
  const copy = (t) => navigator.clipboard?.writeText(t).then(() => toast.success("Copiato"));
  return (
    <Card className="space-y-3 rounded-2xl border-border/60 p-6" data-testid="pu-payment">
      <div className="flex items-center gap-2 font-heading text-lg font-semibold text-forest"><CreditCard className="h-5 w-5" /> Pagamento</div>
      {p.mode === "in_attesa" && <p className="text-sm text-muted-foreground" data-testid="pu-payment-pending">{p.message}</p>}
      {p.mode === "partner" && <p className="text-sm text-forest" data-testid="pu-payment-partner">{p.message}</p>}
      {p.mode === "pagato" && <p className="flex items-center gap-2 text-sm font-semibold text-terracotta" data-testid="pu-payment-paid"><CheckCircle2 className="h-4 w-4" />{p.message}</p>}
      {p.mode === "agenzia" && (
        <div className="space-y-2 text-sm" data-testid="pu-payment-agenzia">
          <div>Bonifico a: <b className="text-forest">{p.intestatario}</b></div>
          <button onClick={() => copy(p.iban)} className="font-mono text-forest underline-offset-2 hover:underline" data-testid="pu-payment-iban">IBAN {p.iban}</button>
          <p className="text-xs text-muted-foreground">Un bonifico per ciascuna proposta, con la causale indicata:</p>
        </div>
      )}
      {p.righe?.map((r) => (
        <div key={r.numero_proposta} className="rounded-lg bg-sage p-3 text-sm" data-testid={`pu-payment-row-${r.numero_proposta}`}>
          <div className="flex justify-between font-semibold text-forest"><span>{r.product_name}</span><span>{eur(r.importo)}</span></div>
          <div className="text-xs text-muted-foreground">Proposta n. {r.numero_proposta}</div>
          {r.causale && <button onClick={() => copy(r.causale)} className="mt-1 text-left text-xs text-forest hover:underline">Causale: {r.causale}</button>}
        </div>
      ))}
      {p.totale ? <div className="flex justify-between border-t pt-2 text-sm"><span className="text-muted-foreground">Totale da versare</span><b className="text-forest" data-testid="pu-payment-total">{eur(p.totale)}</b></div> : null}
    </Card>
  );
}

export default function ProposalBundle() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [sel, setSel] = useState([]);
  const [letti, setLetti] = useState(false);
  const [otpSent, setOtpSent] = useState(false);
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get(`/public/proposta-unica/${token}`).then(({ data }) => {
    setData(data); setSel(data.items.filter((i) => i.accettabile).map((i) => i.id));
  }).catch((e) => setErr(apiError(e))), [token]);
  useEffect(() => { load(); }, [load]);

  const run = async (fn) => { setBusy(true); try { await fn(); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); } };
  const sendOtp = () => run(async () => {
    const { data: r } = await api.post(`/public/proposta-unica/${token}/otp/invia`);
    setOtpSent(true);
    toast.success(r.sms_inviato ? `Codice inviato via SMS al numero ${r.phone_mask}` : "Codice generato (SMS non disponibile: contatta il consulente)");
  });
  const accept = () => run(async () => {
    const { data: r } = await api.post(`/public/proposta-unica/${token}/accetta`, { accettati: sel, documenti_letti: letti, code });
    toast.success(r.message); await load();
  });

  if (err) return <div className="grid min-h-screen place-items-center bg-bone p-6" data-testid="pu-error"><Card className="max-w-md p-8 text-center"><p className="font-semibold text-forest">Proposta non disponibile</p><p className="mt-2 text-sm text-muted-foreground">{err}</p></Card></div>;
  if (!data) return <div className="grid min-h-screen place-items-center bg-bone"><Loader2 className="h-6 w-6 animate-spin text-forest" /></div>;
  const done = !!data.esito;
  const tot = data.items.filter((i) => (done ? i.accettata : sel.includes(i.id))).reduce((s, i) => s + (i.premio || 0), 0);
  const canAccept = sel.length > 0 && letti && (!data.otp_disponibile || (otpSent && code.length >= 4));

  return (
    <div className="min-h-screen bg-bone px-4 py-8">
      <div className="mx-auto w-full max-w-xl space-y-5">
        <Card className="rounded-2xl border-border/60 p-6" data-testid="pu-card">
          <div className="mb-4 flex items-center gap-2 text-terracotta"><ShieldCheck className="h-5 w-5" /><span className="text-sm font-semibold uppercase tracking-wide">Assietica</span></div>
          <h1 className="font-heading text-2xl font-semibold text-forest">La tua proposta assicurativa</h1>
          <p className="mt-1 text-sm text-muted-foreground">Contraente: <b className="text-forest">{data.contraente}</b></p>
          {done ? (
            <div className="mt-4 rounded-xl bg-sage p-4 text-sm text-forest" data-testid="pu-done">
              <b>Grazie, la sua scelta è stata registrata.</b> Qui sotto trova le istruzioni di pagamento non appena disponibili.
            </div>
          ) : <p className="mt-4 text-sm text-forest">Consulti la documentazione di ogni prodotto e scelga quali accettare.</p>}
        </Card>
        {data.pagamento && <PaymentBox p={data.pagamento} />}
        <div className="space-y-3">
          {data.items.map((it) => <ProductRow key={it.id} it={it} locked={done} checked={sel.includes(it.id)}
            onToggle={() => setSel((s) => (s.includes(it.id) ? s.filter((x) => x !== it.id) : [...s, it.id]))} />)}
        </div>
        <Card className="space-y-4 rounded-2xl border-border/60 p-6">
          <div className="flex justify-between text-sm"><span className="text-muted-foreground">{done ? "Totale prodotti accettati" : "Totale prodotti selezionati"}</span><b className="text-forest" data-testid="pu-total">{eur(tot)}</b></div>
          {!done && (<>
            <label className="flex items-start gap-3 text-sm text-forest">
              <Checkbox checked={letti} onCheckedChange={(v) => setLetti(!!v)} data-testid="pu-letti" className="mt-0.5" />
              Dichiaro di aver ricevuto e letto la documentazione precontrattuale e contrattuale dei prodotti selezionati.
            </label>
            {data.otp_disponibile && (
              <div className="space-y-2 rounded-xl border p-3">
                <div className="flex items-center gap-2 text-sm text-forest"><Smartphone className="h-4 w-4" /> Conferma con un codice via SMS al numero {data.phone_mask}</div>
                <div className="flex gap-2">
                  <Button variant="outline" onClick={sendOtp} disabled={busy} data-testid="pu-otp-send">{otpSent ? "Invia di nuovo" : "Invia codice"}</Button>
                  <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} maxLength={6} placeholder="Codice" data-testid="pu-otp-code" />
                </div>
              </div>
            )}
            <Button onClick={accept} disabled={!canAccept || busy} className="w-full bg-terracotta hover:bg-terracotta-hover" data-testid="pu-accept">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : `Accetta ${sel.length} ${sel.length === 1 ? "prodotto" : "prodotti"}`}
            </Button>
          </>)}
        </Card>
      </div>
    </div>
  );
}
